// BL-177 Step 2l: the pop-ups that follow a person around the app.
//   Allow / Decline   "<Name> wants to join your conversation with <client>" (and the same for an invite or a pass), with the
//                     note. It stays until the person answers: nothing is ever allowed on a timeout.
//                     (Step 2j: built on the platform popup template, Modal3D; it cannot be dismissed, only answered.)
//   "<Name> joined"   a short alert for the people already in a conversation when someone comes in.
//   "<Host> closed the group conversation"   a short alert for a person the host removed (Step 2j).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Button3D from '../ui/Button3D';
import Modal3D from '../ui/Modal3D';
import useConversationAccess, { announceAccessChanged } from './useConversationAccess';
import { decideRequest, joinedAlertText, listParticipants, removedAlertText, requestPopupTitle } from '../../lib/conversationAccess';

export const JOINED_ALERT_MS = 5000;

export default function ConversationAccessPopups({ client, userId, onOpenConversation, pollMs = 20000 }) {
    const { requests, mineIds, reload } = useConversationAccess({ client, userId, pollMs });
    const [busyId, setBusyId] = useState('');
    const [errors, setErrors] = useState({});
    const [joined, setJoined] = useState([]);
    const mineRef = useRef(mineIds);
    useEffect(() => { mineRef.current = mineIds; }, [mineIds]);

    const incoming = requests.filter((request) => request.direction === 'incoming' && request.status === 'pending');

    const answer = useCallback(async (request, allow) => {
        setBusyId(request.id);
        setErrors((current) => ({ ...current, [request.id]: '' }));
        try {
            await decideRequest(client, { requestId: request.id, allow });
            await reload();
            announceAccessChanged();
            if (allow && request.kind !== 'join_request' && request.session_id) onOpenConversation?.(request.session_id);
        } catch (failure) {
            setErrors((current) => ({ ...current, [request.id]: failure.message }));
            reload();
        } finally {
            setBusyId('');
        }
    }, [client, onOpenConversation, reload]);

    // "<Name> joined": a new participant row in a conversation I am in (row-level security only delivers rows I may see).
    useEffect(() => {
        if (!client || !userId || typeof client.channel !== 'function') return undefined;
        let channel = null;
        let alive = true;
        const timers = [];
        try {
            channel = client.channel(`ia-conv-joined-${userId}`)
                .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'inbox_conversation_members' }, async (change) => {
                    const row = change?.new || {};
                    if (!row.session_id || row.user_id === userId || !mineRef.current.has(row.session_id)) return;
                    let name = '';
                    try { name = (await listParticipants(client, row.session_id)).find((person) => person.user_id === row.user_id)?.name || ''; } catch { /* name is a nicety */ }
                    if (!alive) return;
                    const id = `${row.session_id}:${row.user_id}:${Date.now()}`;
                    setJoined((current) => [...current, { id, text: joinedAlertText(name), sessionId: row.session_id }]);
                    timers.push(setTimeout(() => setJoined((current) => current.filter((item) => item.id !== id)), JOINED_ALERT_MS));
                })
                .subscribe();
        } catch { /* no live channel: the people panel still shows who is in */ }
        return () => {
            alive = false;
            timers.forEach(clearTimeout);
            try { if (channel) client.removeChannel(channel); } catch { /* already closed */ }
        };
    }, [client, userId]);

    // BL-177 Step 2j (B1): a person the host removed (Close group conversation) gets a short alert. The database writes one
    // 'group_closed' event per removed person with that person as the subject, which row-level security delivers to them alone.
    useEffect(() => {
        if (!client || !userId || typeof client.channel !== 'function') return undefined;
        let channel = null;
        const timers = [];
        let alive = true;
        try {
            channel = client.channel(`ia-conv-removed-${userId}`)
                .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'conversation_access_events' }, (change) => {
                    const row = change?.new || {};
                    if (!alive || row.event !== 'group_closed' || row.subject_id !== userId) return;
                    const id = `removed:${row.id || row.session_id}:${Date.now()}`;
                    setJoined((current) => [...current, { id, text: removedAlertText(row.detail), sessionId: row.session_id, removed: true }]);
                    timers.push(setTimeout(() => setJoined((current) => current.filter((item) => item.id !== id)), JOINED_ALERT_MS * 2));
                })
                .subscribe();
        } catch { /* the people list still updates on its own */ }
        return () => {
            alive = false;
            timers.forEach(clearTimeout);
            try { if (channel) client.removeChannel(channel); } catch { /* already closed */ }
        };
    }, [client, userId]);

    if (!incoming.length && !joined.length) return null;
    const current = incoming[0] || null;
    const waiting = incoming.length - 1;
    return (
        <>
            {joined.length > 0 && (
                <div className="fixed bottom-4 right-4 z-[60] flex w-[min(26rem,calc(100vw-2rem))] flex-col gap-2" data-conversation-popups="true">
                    {joined.map((item) => (
                        <div key={item.id} role="status" aria-live="polite" className="ia-d2 ia-popup3d rounded-2xl px-4 py-2 text-sm font-bold" {...(item.removed ? { 'data-removed-alert': 'true' } : { 'data-joined-alert': 'true' })}>
                            {item.text}
                        </div>
                    ))}
                </div>
            )}
            {current && (
                <Modal3D
                    key={current.id}
                    title={requestPopupTitle(current)}
                    subtitle={current.kind === 'join_request' ? 'Nothing happens until you choose.' : 'You choose: nothing changes until you answer.'}
                    role="alertdialog"
                    dismissible={false}
                    testId="conversation-request-popup"
                    data-access-request={current.id}
                    actions={(
                        <>
                            <Button3D onClick={() => answer(current, false)} disabled={busyId === current.id}>Decline</Button3D>
                            <Button3D variant="go" onClick={() => answer(current, true)} disabled={busyId === current.id}>Allow</Button3D>
                        </>
                    )}
                >
                    <p className="break-words text-sm text-white" data-access-note="true">“{current.note}”</p>
                    {errors[current.id] && <p role="alert" className="mt-1 text-sm text-[#FCA5A5]">{errors[current.id]}</p>}
                    {waiting > 0 && <p className="mt-2 text-xs text-white" data-more-waiting="true">+{waiting} more waiting</p>}
                </Modal3D>
            )}
        </>
    );
}
